import { romeDateString, shiftDate } from './daily';

/**
 * The game's address moved, from gran-premio.camilomercadogta.workers.dev to grandprem.io (October 2026).
 * A browser keeps a site's saved data per address, so a player opening the old one would arrive at the new
 * one as a stranger: no name, a new id (their laps on the board no longer theirs), the lesson offered again,
 * counted as new in the stats. So the old address hands what it kept to the new one, in the link's #fragment
 * (a browser never sends that to a server): the player, the settings, and today's and yesterday's days (the
 * only ones the boards keep). The new address takes it in before anything reads storage (src/game/moveFirst.ts
 * runs ahead of the rest of the game).
 */
export const HOME = import.meta.env?.VITE_MOVE_TO || 'https://grandprem.io';
/** The old address (a test run can stand in for it: VITE_MOVE_FROM, with VITE_MOVE_TO the new one). */
const OLD_HOSTS = [import.meta.env?.VITE_MOVE_FROM || 'gran-premio.camilomercadogta.workers.dev'];

const MARK = '#move=';
const DAY = 'granpremio:v2:day:';
const PLAYER = 'granpremio:setting:player';
const PLAYER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export type Store = Pick<Storage, 'getItem' | 'setItem' | 'key' | 'length'>;

/** The game's own keys (the rest of a page's storage is none of its business). */
const ours = (k: string): boolean => k.startsWith('granpremio:') || k.startsWith('gp.');

/**
 * What this browser kept, for the new address (null if nothing): everything but the days before yesterday
 * (a medal won on one of those is remembered, so the lesson isn't suggested again).
 */
export function pack(store: Store, today: string): string | null {
  const keep = new Set([today, shiftDate(today, -1)]);
  const items: Record<string, string> = {};
  let medal = false;
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (!k || !ours(k)) continue;
    const v = store.getItem(k);
    if (v === null) continue;
    if (k.startsWith(DAY) && !keep.has(k.slice(DAY.length).split(':')[0])) {
      medal ||= /"(bronze|silver|gold)":true/.test(v);
      continue;
    }
    items[k] = v;
  }
  if (medal) items['gp.medal'] = '1';
  return Object.keys(items).length ? encode(JSON.stringify({ v: 1, items })) : null;
}

/** A parcel back into its items; null if it isn't one. */
export function unpack(parcel: string): Record<string, string> | null {
  try {
    const o = JSON.parse(decode(parcel)) as { v?: number; items?: Record<string, unknown> };
    if (o?.v !== 1 || !o.items || typeof o.items !== 'object') return null;
    const items: Record<string, string> = {};
    for (const [k, v] of Object.entries(o.items)) if (ours(k) && typeof v === 'string') items[k] = v;
    return items;
  } catch {
    return null;
  }
}

/**
 * Take a parcel in. A browser new to this address takes it all. One that played here already keeps what it
 * chose here: its player, if it has put a lap on a board under a name (else the old player takes over, laps
 * and all), its settings; and of a day kept on both, whichever has the better lap.
 */
export function take(items: Record<string, string>, store: Store): 'moved' | 'kept' {
  const here = player(store.getItem(PLAYER));
  const there = player(items[PLAYER] ?? null);
  const who = there && !here?.name ? 'moved' : 'kept';
  for (const [k, v] of Object.entries(items)) {
    const cur = store.getItem(k);
    if (k === PLAYER) {
      if (who === 'moved') store.setItem(k, v);
    } else if (cur === null) store.setItem(k, v);
    else if (k.startsWith(DAY)) {
      if (lapMs(v) < lapMs(cur)) store.setItem(k, v);
    } else if (k === 'gp.laps') store.setItem(k, String(Math.max(Number(cur) || 0, Number(v) || 0)));
    else if (k === 'gp.days') store.setItem(k, joinDays(cur, v));
  }
  return who;
}

function player(raw: string | null): { id: string; name: string } | null {
  try {
    const p = JSON.parse(raw ?? 'null') as { id?: unknown; name?: unknown } | null;
    return p && typeof p.id === 'string' && PLAYER_ID.test(p.id) ? { id: p.id, name: typeof p.name === 'string' ? p.name : '' } : null;
  } catch {
    return null;
  }
}

/** A day's best lap (ms), Infinity without one. */
function lapMs(raw: string): number {
  try {
    const ms = (JSON.parse(raw) as { best?: { lapMs?: unknown } | null }).best?.lapMs;
    return typeof ms === 'number' ? ms : Infinity;
  } catch {
    return Infinity;
  }
}

/** The stats' days on this browser (net/statsReporter.ts) from both addresses: its first day, last day and the one before. */
function joinDays(a: string, b: string): string {
  const parse = (raw: string): { first: string; day: string; prev: string } | null => {
    try {
      const o = JSON.parse(raw) as { first?: unknown; day?: unknown; prev?: unknown };
      return typeof o.first === 'string' && typeof o.day === 'string' && typeof o.prev === 'string' ? (o as { first: string; day: string; prev: string }) : null;
    } catch {
      return null;
    }
  };
  const x = parse(a);
  const y = parse(b);
  if (!x || !y) return x ? a : b;
  const day = x.day > y.day ? x.day : y.day;
  const before = [x.day, x.prev, y.day, y.prev].filter((d) => d && d < day).sort();
  // '' first: it played before the stats began.
  const first = x.first === '' || y.first === '' ? '' : x.first < y.first ? x.first : y.first;
  return JSON.stringify({ first, day, prev: before.at(-1) ?? '' });
}

function encode(s: string): string {
  let bin = '';
  for (const b of new TextEncoder().encode(s)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decode(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '==='.slice((b64.length + 3) % 4));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
}

function local(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Run first thing. On the old address: off to the new one with what this browser kept ('leaving': the game
 * doesn't start here). On the new one, arriving with a parcel: take it in ('arrived'). Otherwise null.
 */
export function startMove(): 'leaving' | 'arrived' | null {
  if (typeof location === 'undefined') return null;
  if (OLD_HOSTS.includes(location.hostname)) {
    leave();
    return 'leaving';
  }
  return location.hash.startsWith(MARK) && arrive() ? 'arrived' : null;
}

function leave(): void {
  let to = HOME + location.pathname + location.search;
  if (location.hash) to += location.hash; // a link's own fragment (the stats page's key) goes as it is
  else {
    try {
      const s = local();
      const parcel = s ? pack(s, romeDateString()) : null;
      if (parcel) to += MARK + parcel;
    } catch {
      // Nothing to carry: off without it.
    }
  }
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true || matchMedia('(display-mode: standalone)').matches;
  if (standalone) movedCard(to);
  else location.replace(to);
}

function arrive(): boolean {
  const parcel = location.hash.slice(MARK.length);
  // Off the address bar and out of the history straight away, whatever happens next.
  history.replaceState(history.state, '', location.pathname + location.search);
  try {
    const s = local();
    const items = unpack(parcel);
    if (!s || !items) return false;
    // A parcel that didn't come from the old address (a link someone made up) may only fill a browser that
    // has nothing of the game's yet: it never replaces a player.
    const fromOld = OLD_HOSTS.some((h) => document.referrer.startsWith(`https://${h}/`) || document.referrer.startsWith(`http://${h}:`));
    if (!fromOld) {
      for (let i = 0; i < s.length; i++) if (ours(s.key(i) ?? '')) return false;
    }
    take(items, s);
    s.setItem('gp.moved', JSON.stringify({ at: Date.now() }));
    return true;
  } catch {
    return false;
  }
}

/**
 * A home-screen app on the old address can't hand its data to a new home-screen app (each keeps its own),
 * and jumping to the new address inside the old icon would only confuse: say where the game is now.
 */
function movedCard(to: string): void {
  const show = (): void => {
    document.getElementById('boot')?.remove();
    const card = document.createElement('div');
    card.style.cssText =
      'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:32px;background:#EDC27A;color:#3B2A1E;text-align:center;font-family:"Barlow Condensed",sans-serif;z-index:10';
    card.innerHTML =
      '<div style="font:400 56px/0.95 \'League Gothic\',sans-serif;color:#B8352B">grand premio<br>has moved</div>' +
      '<div style="font:700 22px/1.2 \'Barlow Condensed\',sans-serif;letter-spacing:0.02em">to grandprem.io</div>' +
      '<div style="font:600 17px/1.35 \'Barlow Condensed\',sans-serif;max-width:300px">Open it in Safari, then Share → Add to Home Screen. You can delete this icon.</div>';
    const a = document.createElement('a');
    a.href = to;
    a.target = '_blank';
    a.textContent = 'open grandprem.io';
    a.style.cssText =
      'margin-top:8px;padding:14px 26px 12px;background:#B8352B;color:#F3E6C8;text-decoration:none;font:400 30px/1 \'League Gothic\',sans-serif;letter-spacing:0.02em';
    card.appendChild(a);
    document.body.appendChild(card);
  };
  if (document.body) show();
  else document.addEventListener('DOMContentLoaded', show);
}
